/* eslint-disable max-len */
import { DialogProps, Grid, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Box } from '@mui/system';
import { Link as RouterLink } from 'react-router-dom';
import { NoDataMessage } from "../../components/DOAAuditReportComponents/NoDataMessage";
import { MainContainer } from "../../components/MainContainerComp";
import { ICurrentDispatchLinesData, LinesEmployee, LinesOrderDispatchDetail, LinesOrderHeader } from "./CurrentDispatchLinesData";
import { LineSlip } from "./components/LinesSlip";
import { acceptanceImageStyle, currentDispStatusStyle, greyHeaderStyle, isLoggedInEmployeeStyle, jobPickupStatusStyle, legendStyle, tableWrapper } from "./styled";

export interface DynamicTableProps {
  data: ICurrentDispatchLinesData;
  currentRegistrationNumber?: string;
  handleSubmit: (acceptFlag: 0 | 1) => void;
  selectedOrderDetailId: string;
  handleShowSlip: (id: string) => void;
  handleBackdropClick: DialogProps["onClose"];
}

const DynamicTable = ({
  data,
  currentRegistrationNumber,
  handleSubmit,
  selectedOrderDetailId,
  handleShowSlip,
  handleBackdropClick
}: DynamicTableProps) => {
  if (data.toString().length === 0) return <Typography className="noDataStyle">No data is available at this time. Perhaps the Lines Rotation needs to be run by the dispatcher. </Typography>;
  if (!data.linesRotationGroups || !data.currentEmployeeDispatchInfo || !data.currentEmployeeSlips) {
    return <NoDataMessage />
  }
  const linesOrders = data.linesOrderHeaders || [];
  const onGroupThree = !!data.linesRotationGroups.find(g => g.rotationGroupCode === 'D')?.linesEmployees.find(e => e.registrationNumber === currentRegistrationNumber);
  const isDispatched = !!data.currentEmployeeDispatchInfo[0].isDispatched;

  const hideButtons = !!data.linesRotationGroups.find(group => group.linesEmployees.find(employee => {
    if (employee.registrationNumber === currentRegistrationNumber) {
      return employee.linesOrderDispatchDetails.find(order => order.hasAcceptedJob || order.isDeclinedJob);
    }
    return false;
  }));

  const getAcceptanceImageSrc = (orderDetail: LinesOrderDispatchDetail, employee: LinesEmployee, order: LinesOrderHeader) => {
    if (
      (orderDetail.isAssigned && orderDetail.hasAcceptedJob && currentRegistrationNumber === employee.registrationNumber && order.orderTypeCode === 'LG') ||
      (orderDetail.isAssigned && !orderDetail.hasAcceptedJob && !orderDetail.isSkipped && !orderDetail.isDeclinedJob && currentRegistrationNumber === employee.registrationNumber && order.orderTypeCode === 'LG')
    ) {
      return `/assets/images/lines/v_lg_w.png`;
    } else if (
      (orderDetail.isAssigned && orderDetail.hasAcceptedJob && currentRegistrationNumber === employee.registrationNumber && order.orderTypeCode === 'TU') ||
      (orderDetail.isAssigned && !orderDetail.hasAcceptedJob && !orderDetail.isSkipped && !orderDetail.isDeclinedJob && currentRegistrationNumber === employee.registrationNumber && order.orderTypeCode === 'TU')
    ) {
      return `/assets/images/lines/v_tu_w.png`;
    }
  };

  const getSlip = (curOrder: string) => {
    return data.currentEmployeeSlips?.find(o => o.orderId === Number(curOrder));
  };

  return (
    <MainContainer hasGreyBg>
      <Box sx={tableWrapper}>
        <Box sx={currentDispStatusStyle}>
          {isDispatched && 
            data.currentEmployeeDispatchInfo.map(info => (
              <Typography key={info.registrationNumber}>Job Order Pickup</Typography>
            ))
          }
          {!isDispatched && onGroupThree &&
            data.currentEmployeeDispatchInfo.map(info => (
              <Typography key={info.registrationNumber}>
                You are currently on days off. You will be moved to rotation one on {info.nextRotationStartWhenDayOff}
              </Typography>
            ))
          }
          {!isDispatched && !onGroupThree &&
            data.currentEmployeeDispatchInfo.map(info => (
              <Grid container key={info.registrationNumber}>
                <Grid item xs={12}>
                  <Typography key={info.registrationNumber}>
                    Positions ordered (24hr): {info.totalPositions}
                  </Typography>
                  <Typography key={info.registrationNumber}>
                    Positions from dispatch: {info.nextDispatchSequence}
                  </Typography>
                  <Typography key={info.registrationNumber}>
                    Estimated dispatch: {info.nextDispatchOrderDate}
                  </Typography>
                  <Link key={info.registrationNumber} component={RouterLink} to="/lines-work-info">
                    {info.nextDispatchJobDesc}
                  </Link>
                </Grid>
              </Grid>
            ))
          }
        </Box>
        <Grid container sx={legendStyle}>
          <Grid item xs={1.8}>
            <Box sx={{ bgcolor: '#D0F5DA', textAlign: 'center', border: '1px solid #b8b8b8', width: 100, height: 20 }}>Accepted</Box>
          </Grid>
          <Grid item xs={1.8}>
            <Box sx={{ bgcolor: '#FCB00A', textAlign: 'center', border: '1px solid #b8b8b8', width: 100, height: 20 }}>Assigned</Box>
          </Grid>
          <Grid item xs={1.8}>
            <Box sx={{ bgcolor: '#616161', textAlign: 'center', color: '#ffffff', border: '1px solid #b8b8b8', width: 100, height: 20 }}>Skipped</Box>
          </Grid>
          <Grid item xs={1.8}>
            <Box sx={{ bgcolor: '#F53105', textAlign: 'center', color: '#ffffff', border: '1px solid #b8b8b8', width: 100, height: 20 }}>Refused</Box>
          </Grid>
        </Grid>
        {data.linesRotationGroups.map(group => (
          <Table size="custom" sx={{ width: 'max-content' }} key={group.rotationGroupCode}>
            <TableHead sx={greyHeaderStyle}>
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                  ROTATION
                  <Typography component={'span'} sx={{ marginRight: '5px' }}>
                    {group.rotationGroupCode === 'D' ? ' 3' : ' ' + group.rotationGroupCode}
                  </Typography>
                  -
                  {group.rotationGroupCode === 'D' ? ' DAYS OFF' : ' WORKING'}
                </TableCell>
                {linesOrders.map(order => (
                  <TableCell key={order.orderNumber} sx={{ textAlign: 'center' }}>
                    <Typography>{order.orderTypeCode} {order.gangSize}</Typography>
                    <Typography>{order.startTime}</Typography>
                  </TableCell>
                )) || []}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.linesRotationGroups?.find(g => g.rotationGroupCode === group.rotationGroupCode)?.linesEmployees.map(employee => (
                <TableRow className={currentRegistrationNumber === employee.registrationNumber ? 'currentEmployee' : ''} sx={isLoggedInEmployeeStyle} key={employee.registrationNumber}>
                  <TableCell className={employee.isNATC ? 'natc' : 'notnatc'}>
                    {parseInt(employee.listOrder) > 30 ? (parseInt(employee.listOrder) - 30).toString() : parseInt(employee.listOrder) > 15 ? (parseInt(employee.listOrder) - 15).toString() : employee.listOrder}
                  </TableCell>
                  <TableCell className={`${employee.hasWorkingButton ? 'isWorkingButtonStyle' : ''} ${employee.hasDayOffButton && employee.rotationGroupCode == 'D' ? 'isDayOffButtonStyle' : ''}`}></TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{employee.registrationNumber}</TableCell>
                  <TableCell>{employee.name}</TableCell>
                  {linesOrders.map((order, i) => {
                    const orderDetail = employee.linesOrderDispatchDetails.find(detail => detail.orderNumber === order.orderNumber);
                    return (
                      <TableCell sx={{ position: 'relative' }} className="orderDetail" key={order.orderNumber}>
                        {orderDetail ? (
                          <Typography
                            className={`${orderDetail.isAssigned && orderDetail.hasAcceptedJob ? 'isAcceptedStyle' : ''}
                              ${orderDetail.isAssigned && !orderDetail.hasAcceptedJob && !orderDetail.isSkipped && !orderDetail.isDeclinedJob ? 'isAssignedStyle' : ''}
                              ${orderDetail.isDeclinedJob ? 'isDeclinedStyle' : ''}
                              ${orderDetail.isAssigned && orderDetail.isSkipped ? 'isSkippedStyle' : ''}`}
                            sx={jobPickupStatusStyle}
                          >
                            {orderDetail.isSkippedNATC ? 'NATC' : ''}
                            {orderDetail.isSkippedOther ? 'OTHER' : ''}
                            {orderDetail.isSkippedRestriction ? 'RESTRICTED' : ''}
                            {orderDetail.isSkippedTSC ? 'TSC' : ''}
                            <img
                              onClick={() => handleShowSlip(orderDetail.orderDetailId)}
                              src={getAcceptanceImageSrc(orderDetail, employee, order)}
                              alt=''
                              style={acceptanceImageStyle}
                            />
                            {currentRegistrationNumber === employee.registrationNumber &&
                              selectedOrderDetailId === orderDetail.orderDetailId &&
                              (
                                <LineSlip
                                  data={getSlip(order.orderNumber)}
                                  open={selectedOrderDetailId === orderDetail.orderDetailId}
                                  hideButtons={hideButtons}
                                  onClose={() => handleShowSlip('')}
                                  onBackdropClick={handleBackdropClick}
                                  handleSubmit={handleSubmit}
                                />
                              )}
                          </Typography>
                        ) : ''}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )) || []}
            </TableBody>
          </Table>
        )) || []}
      </Box>
    </MainContainer>
  );
};

export default DynamicTable;
